import { Fragment, useMemo, useRef, useState } from 'react';

import { Button, Stack, Typography } from '@mui/material';

import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import sortBy from 'lodash/sortBy';

import ExternalLinkIcon from '@/components/Icons/ExternalLinkIcon';
import ToolIcon from '@/components/Icons/ToolIcon';
import useIsOrgNavBar2025Enabled from '@/hooks/useIsOrgNavBar2025Enabled';
import useIsTrainingEnabled from '@/hooks/useIsTrainingEnabled';
import { links } from '@/localization';
import { mapTrainingJobStatusToGroup } from '@/sections/Training/utils';
import ListOptions from '@/sections/Workspace/Filter/ListOptions';
import Search from '@/sections/Workspace/Filter/Search';
import ViewModes from '@/sections/Workspace/Filter/ViewModes';
import { ViewMode } from '@/sections/Workspace/types';
import Fuse from 'fuse.js';
import moment from 'moment';

import { BASE_FUSE_SEARCH_OPTIONS, mapSortByTrainingToTitle } from '../constants';
import { useFilterQuery, useTableContainerHeight, useViewMode } from '../hooks';
import EmptyState, { Loading } from '../sections/EmptyStates';
import { IconContainer } from '../styled';
import { SortByTraining, WorkspaceSections } from '../types';
import TrainingEmptyState from './EmptyState';
import Grid from './Grid';
import Table from './Table';
import { WorkspaceTrainingJobFragment } from './__generated__/fragments.generated';
import { TRAINING_JOB_FILTERS } from './constants';
import { useTrainingJobs } from './hooks';
import { LegacyTrainingContainer } from './styled';
import { Filter, TrainingProps } from './types';

const FUSE_SEARCH_OPTIONS = {
  ...BASE_FUSE_SEARCH_OPTIONS,
  keys: ['name', 'id'],
};

function Training({ minimal = false }: TrainingProps) {
  const { trainingJobs, loading } = useTrainingJobs(true);
  const { viewMode, setViewMode } = useViewMode(WorkspaceSections.Training, minimal);
  const { filterQuery, setFilterQuery } = useFilterQuery(WorkspaceSections.Training);
  const [selectedFilter, setSelectedFilter] = useState<Filter>('ALL');
  const [selectedSortBy, setSelectedSortBy] = useState<SortByTraining>(
    SortByTraining.RECENTLY_ACTIVE,
  );
  const isTrainingEnabled = useIsTrainingEnabled();
  const containerRef = useRef<HTMLDivElement>(null);
  const tableContainerHeight = useTableContainerHeight(containerRef, Number(loading));
  const isOrgNavBar2025Enabled = useIsOrgNavBar2025Enabled();

  const sortedTrainingJobs = useMemo(() => {
    if (selectedSortBy === SortByTraining.RECENTLY_ACTIVE) {
      return sortBy(trainingJobs, (trainingJob) =>
        moment(trainingJob.modified).valueOf(),
      ).reverse();
    }

    return sortBy(trainingJobs, (trainingJob) => moment(trainingJob.created).valueOf()).reverse();
  }, [trainingJobs, selectedSortBy]);

  const filteredTrainingJobs = useMemo(() => {
    const filteredByStatus =
      selectedFilter === 'ALL'
        ? sortedTrainingJobs
        : sortedTrainingJobs.filter(
            (trainingJob) =>
              mapTrainingJobStatusToGroup(trainingJob.status.value) === selectedFilter,
          );

    if (!filterQuery) {
      return filteredByStatus;
    }

    const fuse = new Fuse<WorkspaceTrainingJobFragment>(filteredByStatus, FUSE_SEARCH_OPTIONS);

    return fuse.search(filterQuery).map(({ item }) => item);
  }, [filterQuery, sortedTrainingJobs, selectedFilter]);

  function getContent() {
    if (loading) {
      return <Loading />;
    }

    if (isEmpty(trainingJobs)) {
      return <TrainingEmptyState />;
    }

    if (isEmpty(filteredTrainingJobs)) {
      return <EmptyState>No Training Jobs found</EmptyState>;
    }

    return viewMode === ViewMode.Grid ? (
      <Grid trainingJobs={filteredTrainingJobs} />
    ) : (
      <Table trainingJobs={filteredTrainingJobs} height={tableContainerHeight} minimal={minimal} />
    );
  }

  const Wrapper = isOrgNavBar2025Enabled ? Fragment : LegacyTrainingContainer;

  return (
    <Wrapper>
      <Stack height="100%">
        {!minimal && (
          <Stack gap={3} px={4} pt={4} pb={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" gap={2}>
                <IconContainer>
                  <ToolIcon />
                </IconContainer>
                <Typography variant="h2">Training</Typography>
              </Stack>
              {isTrainingEnabled && (
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<ExternalLinkIcon />}
                  href={links.basetenDocsTrainingOverview}
                  target="_blank"
                >
                  Start a Training job
                </Button>
              )}
            </Stack>
            <Stack direction="row" gap={1} flexWrap="wrap">
              <Search query={filterQuery} setQuery={setFilterQuery} />
              <ListOptions<SortByTraining>
                title="Sort"
                options={Object.values(SortByTraining)}
                selectedOption={selectedSortBy}
                onOptionSelect={setSelectedSortBy}
                mapOptionToTitle={(option) => mapSortByTrainingToTitle[option]}
              />
              <ListOptions<Filter>
                title="Status"
                options={TRAINING_JOB_FILTERS}
                selectedOption={selectedFilter}
                onOptionSelect={setSelectedFilter}
                mapOptionToTitle={(option) => capitalize(option.replace(/_/g, ' '))}
              />
              <ViewModes viewMode={viewMode} setViewMode={setViewMode} />
            </Stack>
          </Stack>
        )}
        <Stack flex={1} ref={containerRef} overflow="hidden">
          {getContent()}
        </Stack>
      </Stack>
    </Wrapper>
  );
}

export default Training;
